import React, { useEffect, useMemo, useState } from 'react';
import { teamAPI, uploadAPI } from '../../services/api';
import './AdminComponents.css';

const emptyMemberForm = {
  name: '',
  role: '',
  departmentId: '',
  department: 'Creative Team',
  label: '',
  imageUrl: '',
  slug: '',
  bio: '',
  profileUrl: '',
  displayOrder: 0,
  active: true
};

const emptyDepartmentForm = {
  name: '',
  displayOrder: 0,
  active: true
};

const toMemberForm = (member) => ({
  name: member.name || '',
  role: member.role || '',
  departmentId: member.department_id || '',
  department: member.department || 'Creative Team',
  label: member.label || '',
  imageUrl: member.image_url || '',
  slug: member.slug || '',
  bio: member.bio || '',
  profileUrl: member.profile_url || '',
  displayOrder: member.display_order || 0,
  active: member.active !== false
});

const toDepartmentForm = (department) => ({
  name: department.name || '',
  displayOrder: department.display_order || 0,
  active: department.active !== false
});

const TeamManagement = () => {
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [departmentForm, setDepartmentForm] = useState(emptyDepartmentForm);
  const [loading, setLoading] = useState(true);
  const [savingMember, setSavingMember] = useState(false);
  const [savingDepartment, setSavingDepartment] = useState(false);
  const [uploading, setUploading] = useState(false);

  const selectedMember = useMemo(
    () => members.find((member) => member.id === selectedMemberId),
    [members, selectedMemberId]
  );

  const selectedDepartment = useMemo(
    () => departments.find((department) => department.id === selectedDepartmentId),
    [departments, selectedDepartmentId]
  );

  const sortedDepartments = useMemo(
    () => [...departments].sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name)),
    [departments]
  );

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => {
      const departmentOrder = (a.department_order ?? 999999) - (b.department_order ?? 999999);
      const memberOrder = (a.display_order || 0) - (b.display_order || 0);
      return departmentOrder || String(a.department || '').localeCompare(String(b.department || '')) || memberOrder || a.name.localeCompare(b.name);
    }),
    [members]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersResponse, departmentsResponse] = await Promise.all([
        teamAPI.getAll(),
        teamAPI.getDepartments()
      ]);

      if (membersResponse.success && Array.isArray(membersResponse.data)) {
        setMembers(membersResponse.data);
      }
      if (departmentsResponse.success && Array.isArray(departmentsResponse.data)) {
        setDepartments(departmentsResponse.data);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      alert(error.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setMemberForm(selectedMember ? toMemberForm(selectedMember) : emptyMemberForm);
  }, [selectedMember]);

  useEffect(() => {
    setDepartmentForm(selectedDepartment ? toDepartmentForm(selectedDepartment) : emptyDepartmentForm);
  }, [selectedDepartment]);

  const updateMemberField = (field, value) => {
    setMemberForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateDepartmentField = (field, value) => {
    setDepartmentForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectDepartmentForMember = (departmentId) => {
    const parsedId = departmentId ? Number(departmentId) : '';
    const department = departments.find((item) => item.id === parsedId);
    setMemberForm((prev) => ({
      ...prev,
      departmentId: parsedId,
      department: department ? department.name : prev.department
    }));
  };

  const handleUpload = async (file) => {
    if (!file) return;

    try {
      setUploading(true);
      const response = await uploadAPI.uploadImage(file, 'team');
      if (response.success && response.data?.imageUrl) {
        updateMemberField('imageUrl', response.data.imageUrl);
      }
    } catch (error) {
      console.error('Error uploading portrait:', error);
      alert(error.message || 'Failed to upload portrait');
    } finally {
      setUploading(false);
    }
  };

  const handleDepartmentSubmit = async (event) => {
    event.preventDefault();
    if (uploading || savingMember) return;

    try {
      setSavingDepartment(true);
      if (selectedDepartmentId) {
        await teamAPI.updateDepartment(selectedDepartmentId, departmentForm);
      } else {
        const response = await teamAPI.createDepartment(departmentForm);
        if (response.success && response.data?.id) {
          setSelectedDepartmentId(response.data.id);
        }
      }
      await loadData();
    } catch (error) {
      console.error('Error saving team department:', error);
      alert(error.message || 'Failed to save team department');
    } finally {
      setSavingDepartment(false);
    }
  };

  const handleDepartmentDelete = async () => {
    if (uploading || savingMember) return;
    if (!selectedDepartmentId || !window.confirm('Delete this department category? Staff will keep the department name as legacy text.')) return;

    try {
      await teamAPI.deleteDepartment(selectedDepartmentId);
      setSelectedDepartmentId(null);
      setDepartmentForm(emptyDepartmentForm);
      await loadData();
    } catch (error) {
      console.error('Error deleting team department:', error);
      alert(error.message || 'Failed to delete team department');
    }
  };

  const handleMemberSubmit = async (event) => {
    event.preventDefault();
    if (uploading || savingMember) return;

    try {
      setSavingMember(true);
      if (selectedMemberId) {
        await teamAPI.update(selectedMemberId, memberForm);
      } else {
        const response = await teamAPI.create(memberForm);
        if (response.success && response.data?.id) {
          setSelectedMemberId(response.data.id);
        }
      }
      await loadData();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert(error.message || 'Failed to save team member');
    } finally {
      setSavingMember(false);
    }
  };

  const handleMemberDelete = async () => {
    if (!selectedMemberId || !window.confirm('Delete this team member?')) return;

    try {
      await teamAPI.delete(selectedMemberId);
      setSelectedMemberId(null);
      setMemberForm(emptyMemberForm);
      await loadData();
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert(error.message || 'Failed to delete team member');
    }
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading team...</div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Team</h2>
      </div>

      <div className="admin-info-grid" style={{ alignItems: 'start' }}>
        <div className="admin-settings-card">
          <div className="admin-section-header" style={{ padding: 0, marginBottom: '1rem' }}>
            <h3 className="admin-settings-card-title">Department Categories</h3>
            <button
              className="admin-button primary small"
              type="button"
              onClick={() => {
                setSelectedDepartmentId(null);
                setDepartmentForm(emptyDepartmentForm);
              }}
            >
              New
            </button>
          </div>
          <p className="admin-settings-card-desc">
            These control the department sections and their order on the public Team page.
          </p>

          {sortedDepartments.length === 0 ? (
            <p className="admin-hint">No department categories yet.</p>
          ) : (
            <div className="admin-order-items">
              {sortedDepartments.map((department) => (
                <button
                  key={department.id}
                  type="button"
                  className="admin-order-item"
                  onClick={() => setSelectedDepartmentId(department.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderColor: selectedDepartmentId === department.id ? 'var(--accent)' : undefined
                  }}
                >
                  <div>
                    <strong>{department.name}</strong>
                    <p className="admin-hint" style={{ margin: '0.25rem 0 0' }}>
                      Department Order ID {department.display_order} / {department.active ? 'Visible' : 'Hidden'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleDepartmentSubmit} style={{ marginTop: '1.5rem' }}>
            <h3 className="admin-settings-card-title">{selectedDepartmentId ? 'Edit Department' : 'New Department'}</h3>
            <div className="admin-form-group">
              <label className="admin-form-label">Department Name</label>
              <input className="admin-form-input" value={departmentForm.name} onChange={(e) => updateDepartmentField('name', e.target.value)} required />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Department Order ID</label>
              <input className="admin-form-input" type="number" value={departmentForm.displayOrder} onChange={(e) => updateDepartmentField('displayOrder', e.target.value)} />
              <small className="admin-form-hint">Lower numbers appear higher on the Team page. Keep these unique.</small>
            </div>
            <label className="admin-checkbox-label">
              <input type="checkbox" checked={departmentForm.active} onChange={(e) => updateDepartmentField('active', e.target.checked)} />
              Visible on public Team page
            </label>
            <div className="admin-form-actions" style={{ marginTop: '1.25rem' }}>
              <button type="submit" className="admin-button primary" disabled={savingDepartment || uploading || savingMember}>
                {savingDepartment ? 'Saving...' : 'Save Department'}
              </button>
              {selectedDepartmentId && (
                <button type="button" className="admin-button danger" disabled={savingDepartment || uploading || savingMember} onClick={handleDepartmentDelete}>
                  Delete
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-settings-card">
          <div className="admin-section-header" style={{ padding: 0, marginBottom: '1rem' }}>
            <h3 className="admin-settings-card-title">Staff Members</h3>
            <button
              className="admin-button primary small"
              type="button"
              disabled={uploading || savingMember}
              onClick={() => {
                setSelectedMemberId(null);
                setMemberForm(emptyMemberForm);
              }}
            >
              New
            </button>
          </div>

          {sortedMembers.length === 0 ? (
            <p className="admin-hint">No team members yet.</p>
          ) : (
            <div className="admin-order-items">
              {sortedMembers.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  className="admin-order-item"
                  onClick={() => setSelectedMemberId(member.id)}
                  disabled={uploading || savingMember}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderColor: selectedMemberId === member.id ? 'var(--accent)' : undefined
                  }}
                >
                  <div
                    className="admin-order-item-image"
                    style={{
                      background: 'var(--bg-secondary)',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {member.image_url ? (
                      <img src={member.image_url} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span>{member.name.charAt(0)}</span>
                    )}
                  </div>
                  <div>
                    <strong>{member.name}</strong>
                    <p className="admin-hint" style={{ margin: '0.25rem 0 0' }}>{member.role}</p>
                    <p className="admin-hint" style={{ margin: '0.25rem 0 0' }}>
                      {member.department} / Staff Order ID {member.display_order} / {member.active ? 'Visible' : 'Hidden'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <form className="admin-settings-card" onSubmit={handleMemberSubmit}>
          <h3 className="admin-settings-card-title">{selectedMemberId ? 'Edit Member' : 'New Member'}</h3>

          <div className="admin-form-group">
            <label className="admin-form-label">Name</label>
            <input className="admin-form-input" value={memberForm.name} onChange={(e) => updateMemberField('name', e.target.value)} required />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Role / Title</label>
            <input className="admin-form-input" value={memberForm.role} onChange={(e) => updateMemberField('role', e.target.value)} required />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Department Category</label>
            <select className="admin-form-input" value={memberForm.departmentId || ''} onChange={(e) => selectDepartmentForMember(e.target.value)}>
              <option value="">Legacy / uncategorized</option>
              {sortedDepartments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name} ({department.display_order})
                </option>
              ))}
            </select>
          </div>

          {!memberForm.departmentId && (
            <div className="admin-form-group">
              <label className="admin-form-label">Legacy Department Name</label>
              <input className="admin-form-input" value={memberForm.department} onChange={(e) => updateMemberField('department', e.target.value)} placeholder="Creative Team" />
            </div>
          )}

          <div className="admin-form-group">
            <label className="admin-form-label">Short Label</label>
            <input className="admin-form-input" value={memberForm.label} onChange={(e) => updateMemberField('label', e.target.value)} placeholder="Veloura" />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Portrait URL</label>
            <input className="admin-form-input" type="url" value={memberForm.imageUrl} disabled={uploading || savingMember} onChange={(e) => updateMemberField('imageUrl', e.target.value)} placeholder="https://..." />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Or Upload Portrait</label>
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" disabled={uploading || savingMember} onChange={(e) => {
              handleUpload(e.target.files?.[0]);
              e.target.value = '';
            }} />
            {uploading && <small style={{ color: 'var(--text-secondary)' }}>Uploading...</small>}
          </div>

          {memberForm.imageUrl && (
            <div className="admin-settings-preview">
              <img src={memberForm.imageUrl} alt="Team portrait preview" />
              <button type="button" className="admin-button secondary small" disabled={uploading || savingMember} onClick={() => updateMemberField('imageUrl', '')}>
                Remove
              </button>
            </div>
          )}

          <div className="admin-form-group">
            <label className="admin-form-label">Profile Slug</label>
            <input className="admin-form-input" value={memberForm.slug} onChange={(e) => updateMemberField('slug', e.target.value)} placeholder="auto-generated-from-name" />
            <small className="admin-form-hint">Used for /team/profile-name. Leave blank when creating to auto-generate.</small>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">External Profile URL</label>
            <input className="admin-form-input" type="url" value={memberForm.profileUrl} onChange={(e) => updateMemberField('profileUrl', e.target.value)} placeholder="https://..." />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Bio</label>
            <textarea className="admin-form-input" rows="5" value={memberForm.bio} onChange={(e) => updateMemberField('bio', e.target.value)} />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Staff Order ID</label>
            <input className="admin-form-input" type="number" value={memberForm.displayOrder} onChange={(e) => updateMemberField('displayOrder', e.target.value)} />
            <small className="admin-form-hint">This order ID is unique within the selected department category.</small>
          </div>

          <label className="admin-checkbox-label">
            <input type="checkbox" checked={memberForm.active} onChange={(e) => updateMemberField('active', e.target.checked)} />
            Visible on public Team page
          </label>

          <div className="admin-form-actions" style={{ marginTop: '1.5rem' }}>
            <button type="submit" className="admin-button primary" disabled={savingMember || uploading}>
              {savingMember ? 'Saving...' : 'Save Member'}
            </button>
            {selectedMemberId && (
              <button type="button" className="admin-button danger" disabled={uploading || savingMember} onClick={handleMemberDelete}>
                Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamManagement;
